import { Injectable } from '@angular/core';
import { DropdownListingInterface } from '@app/finxone-web-frontend/app/Modules/shared/widgets/accounts-context-dropdown-widget/accounts-context-dropdown-widget.component';
import {
  AccountStatusType,
  SavingsAccountInfoType,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { GetSavingAccount, UpdateActiveSavingsAccountId } from '../../../actions/savings-account.action';
import { Currency } from '../../../dtos/currencies.dto';
import { SavingsAccountState, SavingsAccountStateModel } from '../../../state/savings-account.state';
import { getCurrencyDataByKey } from '../../../utils/utils';
import { AccountListingStrategyInterface } from '../../interfaces/account-listing-strategy.type';

@Injectable()
export class SavingsAccountsStrategy implements AccountListingStrategyInterface {
  @Select(SavingsAccountState.getAccounts)
  private accountState$: Observable<SavingsAccountStateModel>;
  private accountState: SavingsAccountStateModel;
  private accounts: SavingsAccountInfoType[];
  private selectedAccount: DropdownListingInterface;
  private dropdownListing: DropdownListingInterface[];

  public setupStateSubscription(
    store: Store,
    pageNumber: number,
    pageSize: number,
    accountContextStatusList: AccountStatusType[],
  ): Observable<any> {
    store.dispatch(new GetSavingAccount(pageNumber, pageSize, accountContextStatusList));

    return this.getAccountsStream();
  }

  public getAccountsStream(): Observable<any> {
    return this.accountState$;
  }

  public getAccounts(response: SavingsAccountStateModel) {
    this.accountState = response;
    this.accounts = response.accounts;
    return this.accounts;
  }

  public getDropdownListing(
    response: SavingsAccountStateModel,
    _: Store,
    currencies?: Currency[],
    attributes?: UiZoneWidgetAttributeConfig,
  ) {
    const isFundingEnabled = !!attributes?.['isFundingEnabled'];
    const isWithdrawalEnabled = !!attributes?.['isWithdrawalEnabled'];
    const isClosureEnabled = !!attributes?.['isClosureEnabled'];

    this.dropdownListing = [];
    const listOfAccounts: DropdownListingInterface[] = [];
    response.accounts
      .filter((account) => {
        if (!isFundingEnabled && !isWithdrawalEnabled && !isClosureEnabled) {
          return true;
        }
        // Check if the account satisfies AT LEAST ONE active flag (OR logic)
        const satisfiesFunding = isFundingEnabled && !!account?.isFundingEnabled;
        const satisfiesWithdrawal = isWithdrawalEnabled && !!account?.isWithdrawalEnabled;
        const satisfiesClosable = isClosureEnabled && !!account?.isClosureEnabled;

        return satisfiesFunding || satisfiesWithdrawal || satisfiesClosable;
      })
      .forEach((element: SavingsAccountInfoType, index: number) => {
        const account = this.getSavingAccountData(element, index, currencies);
        listOfAccounts.push(account);
      });

    this.dropdownListing = listOfAccounts;
    return listOfAccounts;
  }

  public getSelectedAccountValueForDropdown(
    store: Store,
    attributes: UiZoneWidgetAttributeConfig,
  ): DropdownListingInterface | null {
    const isPreselectDisabled = attributes?.['isPreselectDisabled'] ?? false;

    const closingAccountId = this.selectedAccount?.accountId;

    if (!closingAccountId && isPreselectDisabled) {
      return null;
    }

    const currentAccountDetails = store.selectSnapshot(
      SavingsAccountState.getCurrentAccountDetails(closingAccountId ?? this.accountState.activeAccountId),
    );

    if (currentAccountDetails) {
      this.selectedAccount = this.getSavingAccountData(currentAccountDetails, 0);
    }
    return this.selectedAccount;
  }

  public getCurrentMultiCurrencyCode(store: Store) {
    return '';
  }

  public selectAccount(account: DropdownListingInterface, store: Store): void {
    this.selectedAccount = account;
    store.dispatch(new UpdateActiveSavingsAccountId(account.accountId));
  }

  public fetchNewAccounts(
    store: Store,
    pageNumber: number,
    pageSize: number,
    accountContextStatusList: AccountStatusType[],
  ): void {
    store.dispatch(new GetSavingAccount(pageNumber, pageSize, accountContextStatusList));
  }

  public clearData(): void {
    this.accountState = {} as SavingsAccountStateModel;
    this.accounts = [];
    this.selectedAccount = {} as DropdownListingInterface;
    this.dropdownListing = [];
  }

  public requiresFullAccountList(attri?: UiZoneWidgetAttributeConfig): boolean {
    const activeFilterKeys: (keyof UiZoneWidgetAttributeConfig)[] = [
      'isFundingEnabled',
      'isWithdrawalEnabled',
      'isClosureEnabled',
    ];
    return activeFilterKeys.some((key) => !!attri?.[key]);
  }

  private getSavingAccountData(
    accountInfo: SavingsAccountInfoType,
    index: number,
    currencies?: Currency[],
  ): DropdownListingInterface {
    return {
      accountCategoryType: 'savings',
      currencyCode: accountInfo.currency,
      currency: accountInfo.currency,
      name: accountInfo.accountName ?? '',
      status: accountInfo.status ?? '',
      country: accountInfo.country ?? '',
      balance: accountInfo.balance,
      accountId: accountInfo.accountId,
      totalBalance: accountInfo.balance,
      accountName: accountInfo.accountName ?? '',
      indicativeBalance: accountInfo.balance,
      availableBalance: accountInfo?.availableBalance ?? '',
      accountBalance: accountInfo?.balance ?? '',
      sortCode: accountInfo.accountIdentifier[0].sortCode ?? '',
      accountCreatedDate: accountInfo?.startDate ?? '',
      currencyFullName:
        currencies?.find((currency: Currency) => currency.currency_code === accountInfo?.currency)
          ?.currency_name ?? 'Unknown Currency',
      ledgerBalance: accountInfo?.balance ?? '',
      ledgerBalanceIncludingPendingExchange: accountInfo?.balance ?? '',
      alias: accountInfo?.accountName ?? '',
      accountNumber: accountInfo.accountIdentifier[0].accountNumber,
      bic: accountInfo.accountIdentifier[0].bic,
      ledgerNumber: accountInfo.accountIdentifier[0].ledgerNumber,
      iban: accountInfo.accountIdentifier[0].iban,
      providerExtraInfo: accountInfo.accountIdentifier[0].providerExtraInfo,
      achAccountNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.ach?.accountNumber ?? '',
      achNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.ach?.routingCodes?.aba ?? '',
      abaAccountNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.wire?.accountNumber ?? '',
      abaNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.wire?.routingCodes?.aba ?? '',
      accountProviderExtraInfo: accountInfo?.accountIdentifier[0]?.providerExtraInfo ?? {},
      pendingExchangeNetTotal: 0,
      pendingExchangeCount: 0,
      pendingTransactionNetTotal: 0,
      pendingTransactionCount: 0,
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, currencies ?? []),
      countryCode: accountInfo.country,
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
      accountType: accountInfo.accountType,
      isFundingEnabled: accountInfo.isFundingEnabled ?? false,
      isWithdrawalEnabled: accountInfo.isWithdrawalEnabled ?? false,
      isClosureEnabled: accountInfo.isClosureEnabled ?? false,
      maxDeposit: accountInfo.maxDeposit ?? '',
      interestRate: accountInfo?.interestRate,
      annualEquivalantInterestRate: accountInfo?.annualEquivalantInterestRate,
    };
  }
}
